import './browser-polyfill'

import type { App } from 'vue'
import { createApp, defineComponent, h } from 'vue'
import type { Pinia } from 'pinia'
import { createPinia } from 'pinia'
import { createRouter, createMemoryHistory, RouterView } from 'vue-router'
import ElementPlus from 'element-plus'
import zhCn from 'element-plus/es/locale/lang/zh-cn'
import 'element-plus/dist/index.css'
import VueCesium from 'vue-cesium'
import 'vue-cesium/dist/index.css'

import MiddleMonitor from '@/views/monitorPage/components/middle-monitor.vue'
import LeftMonitor from '@/views/monitorPage/components/left-monitor.vue'
import {
  applySdkExternalContext,
  clearSdkDefaults,
  getMergedSdkContext,
  setSdkDefaults,
  type SdkExternalContext,
} from '@/microfrontends/monitor-mfe/sdk/apply-store-context'
import {
  attachSdkDeviceWebSocket,
  detachSdkDeviceWebSocket,
} from '@/microfrontends/monitor-mfe/sdk/sdk-device-websocket'
import { clearSdkApiConfig, setSdkApiConfig } from '@/microfrontends/monitor-mfe/sdk/sdk-api-config'
import { clearSdkAuth, setSdkAuth } from '@/microfrontends/monitor-mfe/sdk/sdk-auth'
import { normalizeLeftMonitorMountOptions, type MountMonitorOptions } from '@/microfrontends/monitor-mfe/sdk/sdk-monitor-bootstrap'

/**
 * UMD：<script> 引入后使用 window.MonitorMfeSDK.mountLeftMonitor / mountMiddleMonitor。
 * 仅 default 导出，避免 UMD 全局对象形态异常（methods 落到 .default 或空对象上）。
 */

const KEY_MM = 'middleMonitor'
const KEY_LM = 'leftMonitor'

const registry = new Map<string, App>()

function resolveEl (el: string | HTMLElement): HTMLElement {
  if (typeof el === 'string') {
    const n = document.querySelector(el)
    if (!n) throw new Error(`[MonitorMfeSDK] 未找到挂载节点: ${el}`)
    return n as HTMLElement
  }
  return el
}

function maybeAttachDeviceWebSocket (
  pinia: Pinia,
  merged: ReturnType<typeof getMergedSdkContext>,
  registryKey: string
): void {
  if (!merged) return
  if (
    merged.enableDeviceWebSocket === true ||
    (merged.deviceWebSocketUrl && merged.deviceWebSocketUrl.trim())
  ) {
    attachSdkDeviceWebSocket(pinia, registryKey, merged.deviceWebSocketUrl)
  }
}

function clearSharedSdkRuntimeIfIdle (): void {
  if (registry.size === 0) {
    clearSdkApiConfig()
    clearSdkAuth()
  }
}


function createRouterRoot (
  routeName: string,
  component: typeof MiddleMonitor | typeof LeftMonitor,
  initialQuery?: Record<string, string | string[]>
) {
  const history = createMemoryHistory()
  if (initialQuery && Object.keys(initialQuery).length) {
    const q = new URLSearchParams(initialQuery as Record<string, string>).toString()
    history.replace(q ? `/?${q}` : '/')
  }
  return createRouter({
    history,
    routes: [{ path: '/', name: routeName, component }],
  })
}

function mountSdkModule (
  registryKey: string,
  rootComponentName: string,
  routeName: string,
  pageComponent: typeof MiddleMonitor | typeof LeftMonitor,
  options: MountMonitorOptions
): void {
  unmountSdkModule(registryKey)

  const container = resolveEl(options.el)
  const pinia = createPinia()
  const router = createRouterRoot(routeName, pageComponent, options.initialQuery)

  const SdkRoot = defineComponent({
    name: rootComponentName,
    setup () {
      return () => h(RouterView)
    },
  })

  const app = createApp(SdkRoot)
  app.use(pinia)

  const merged = getMergedSdkContext(options.context)
  applySdkExternalContext(pinia, merged)
  maybeAttachDeviceWebSocket(pinia, merged, registryKey)

  app.use(router)
  app.use(ElementPlus, { locale: zhCn })
  app.use(VueCesium)

  app.mount(container)
  registry.set(registryKey, app)
}

function unmountSdkModule (registryKey: string): void {
  detachSdkDeviceWebSocket(registryKey)
  const app = registry.get(registryKey)
  if (app) {
    app.unmount()
    registry.delete(registryKey)
  }
  clearSharedSdkRuntimeIfIdle()
}

function mountLeftMonitor (options: MountMonitorOptions): void {
  mountSdkModule(
    KEY_LM,
    'SdkLeftMonitorRoot',
    'sdk-left-monitor',
    LeftMonitor,
    normalizeLeftMonitorMountOptions(options)
  )
}

function unmountLeftMonitor (): void {
  unmountSdkModule(KEY_LM)
}

function mountMiddleMonitor (options: MountMonitorOptions): void {
  mountSdkModule(KEY_MM, 'SdkMiddleMonitorRoot', 'sdk-middle-monitor', MiddleMonitor, options)
}

function unmountMiddleMonitor (): void {
  unmountSdkModule(KEY_MM)
}

type SdkModule = 'leftMonitor' | 'middleMonitor'

function mount (module: SdkModule, options: MountMonitorOptions): void {
  if (module === 'leftMonitor') {
    mountLeftMonitor(options)
  } else {
    mountMiddleMonitor(options)
  }
}

function unmount (module?: SdkModule): void {
  if (!module) {
    unmountLeftMonitor()
    unmountMiddleMonitor()
    return
  }
  if (module === 'leftMonitor') unmountLeftMonitor()
  if (module === 'middleMonitor') unmountMiddleMonitor()
}

const MonitorMfeSDK = {
  mount,
  unmount,
  mountLeftMonitor,
  unmountLeftMonitor,
  mountMiddleMonitor,
  unmountMiddleMonitor,
  setSdkDefaults,
  clearSdkDefaults,
  setSdkApiConfig,
  clearSdkApiConfig,
  setSdkAuth,
  clearSdkAuth,
}

function installGlobalMonitorMfeSDK (): void {
  const g = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : undefined
  if (!g) return
  const w = g as unknown as { MonitorMfeSDK?: typeof MonitorMfeSDK & { default?: typeof MonitorMfeSDK } }
  // UMD 旧包可能只有 .default，统一合并到同一全局对象
  const legacy = w.MonitorMfeSDK?.default
  w.MonitorMfeSDK = { ...(legacy || {}), ...MonitorMfeSDK }
}

installGlobalMonitorMfeSDK()

export default MonitorMfeSDK

declare global {
  interface Window {
    MonitorMfeSDK?: typeof MonitorMfeSDK
  }
}
